import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    API_URLS,
    obtenerProductos,
    obtenerUsuarios
} from '../../Services/servicioURL';

interface InventarioItem {
    cve_producto: string;
    descripcion: string;
    modelo: string;
}

interface Usuario {
    id: number;
    nombre: string;
}

// ACTUALIZADO: Interface para el proveedor
interface Proveedor {
    id: number;
    nombre: string;
}

// ACTUALIZADO: Interface para entrada completa con proveedor
interface EntradaCompleta {
    folio: string;
    fecha: string;
    referencia?: string;
    observaciones?: string;
    proveedor?: string; // Nombre del proveedor para mostrar
    id_proveedor?: number; // ID del proveedor
    entradaDetalle: any[];
}

// ACTUALIZADO: Interface para la respuesta del backend
interface EntradaBackend {
    folio: string;
    fecha: string;
    referencia?: string;
    observaciones?: string;
    id_proveedor?: number;
    proveedor?: Proveedor; // Objeto completo del proveedor
    entradaDetalle: any[];
}

const Facturas: React.FC = () => {
    const [productosCatalogo, setProductosCatalogo] = useState<InventarioItem[]>([]);
    const [usuariosCatalogo, setUsuariosCatalogo] = useState<Usuario[]>([]);
    const [referenciaFilter, setReferenciaFilter] = useState<string>('');
    const [proveedorFilter, setProveedorFilter] = useState<string>(''); // NUEVO: Filtro por proveedor
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [todasLasEntradas, setTodasLasEntradas] = useState<EntradaCompleta[]>([]);
    const [filteredEntradas, setFilteredEntradas] = useState<EntradaCompleta[]>([]);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [selectedMovimiento, setSelectedMovimiento] = useState<any>(null);
    const toast = useRef<Toast>(null);

    useEffect(() => {
        const fetchCatalogos = async () => {
            try {
                const [dataProductos, dataUsuarios] = await Promise.all([
                    obtenerProductos(API_URLS.productos.obtener),
                    obtenerUsuarios(API_URLS.usuarios.obtener)
                ]);
                
                const inventarioData = dataProductos.map((producto: any) => ({
                    cve_producto: producto.cve,
                    descripcion: producto.descripcion,
                    modelo: producto.modelo
                }));
                setProductosCatalogo(inventarioData);
                
                const usuariosData = dataUsuarios.map((usuario: any) => ({
                    id: usuario.id,
                    nombre: usuario.nombre
                }));
                setUsuariosCatalogo(usuariosData);
                
            } catch (error) {
                console.error('Error al cargar catálogos:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron cargar los catálogos',
                    life: 3000
                });
            }
        };
        fetchCatalogos();
    }, []);

    // ACTUALIZADO: Cargar todas las entradas con información del proveedor
    useEffect(() => {
        const fetchTodasLasEntradas = async () => {
            setLoading(true);
            setError(null);

            try {
                const urlEntradas = API_URLS.entradas.obtener;
                const response = await fetch(urlEntradas);

                if (!response.ok) {
                    throw new Error('Error al obtener todas las entradas');
                }

                const dataEntradas: EntradaBackend[] = await response.json();
                
                // ACTUALIZADO: Transformar los datos para incluir el nombre del proveedor
                const entradasTransformadas: EntradaCompleta[] = dataEntradas.map(entrada => ({
                    folio: entrada.folio,
                    fecha: entrada.fecha,
                    referencia: entrada.referencia,
                    observaciones: entrada.observaciones,
                    proveedor: entrada.proveedor?.nombre || 'Sin proveedor',
                    id_proveedor: entrada.id_proveedor,
                    entradaDetalle: entrada.entradaDetalle || []
                }));

                setTodasLasEntradas(entradasTransformadas);
                setFilteredEntradas(entradasTransformadas);

                toast.current?.show({
                    severity: 'success',
                    summary: 'Carga exitosa',
                    detail: `Se cargaron ${entradasTransformadas.length} facturas de entrada`,
                    life: 3000
                });

            } catch (err) {
                console.error('Error al cargar todas las entradas:', err);
                setError('No se pudieron cargar las facturas de entrada.');
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron cargar las facturas de entrada.',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        fetchTodasLasEntradas();
    }, []);

    // ACTUALIZADO: Efecto para filtrar entradas cuando cambian los filtros
    useEffect(() => {
        let filtered = todasLasEntradas;

        // Filtrar por referencia
        if (referenciaFilter.trim()) {
            filtered = filtered.filter(entrada => 
                entrada.referencia?.toLowerCase().includes(referenciaFilter.toLowerCase())
            );
        }

        // NUEVO: Filtrar por proveedor
        if (proveedorFilter.trim()) {
            filtered = filtered.filter(entrada => 
                entrada.proveedor?.toLowerCase().includes(proveedorFilter.toLowerCase())
            );
        }

        setFilteredEntradas(filtered);
    }, [referenciaFilter, proveedorFilter, todasLasEntradas]);

    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? '-' : date.toISOString().split('T')[0];
    };

    const getDescripcionProducto = (cve_producto: string): string => {
        const producto = productosCatalogo.find(p => p.cve_producto === cve_producto);
        return producto ? producto.descripcion : 'Producto no encontrado';
    };

    const onReferenciaFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setReferenciaFilter(e.target.value);
    };

    // NUEVO: Función para manejar el filtro de proveedor
    const onProveedorFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProveedorFilter(e.target.value);
    };

    const showMovimientoDetail = (rowData: EntradaCompleta) => {
        setSelectedMovimiento(rowData);
        setIsModalVisible(true);
    };

    // ACTUALIZADA: Exportar tabla principal a Excel con proveedor
    const exportTableToExcel = () => {
        try {
            const excelData = filteredEntradas.map(entrada => {
                const totalProductos = entrada.entradaDetalle?.length || 0;
                const totalCantidad = (entrada.entradaDetalle || []).reduce((sum: number, detalle: any) => sum + detalle.cantidad, 0);
                
                return {
                    'Folio': entrada.folio,
                    'Fecha': formatDate(entrada.fecha),
                    'Proveedor': entrada.proveedor || '-', // NUEVO: Columna de proveedor
                    'Referencia': entrada.referencia || '-',
                    'Total Productos': totalProductos,
                    'Total Cantidad': totalCantidad,
                    'Observaciones': entrada.observaciones || '-'
                };
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // ACTUALIZADO: Configurar ancho de columnas incluyendo proveedor
            const colWidths = [
                { wch: 12 }, // Folio
                { wch: 12 }, // Fecha
                { wch: 25 }, // Proveedor
                { wch: 20 }, // Referencia
                { wch: 15 }, // Total Productos
                { wch: 15 }, // Total Cantidad
                { wch: 40 }  // Observaciones
            ];
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, 'Facturas de Entrada');

            const fecha = new Date().toISOString().slice(0, 10);
            const filtroTexto = referenciaFilter || proveedorFilter ? 
                `_filtrado_${referenciaFilter || ''}${proveedorFilter || ''}` : '';
            const nombreArchivo = `facturas_entrada_BBT_SIS${filtroTexto}_${fecha}.xlsx`;

            XLSX.writeFile(wb, nombreArchivo);

            toast.current?.show({
                severity: 'success',
                summary: 'Exportación exitosa',
                detail: 'Archivo Excel generado correctamente',
                life: 3000
            });

        } catch (error) {
            console.error('Error al generar Excel:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Ocurrió un error al generar el archivo Excel',
                life: 3000
            });
        }
    };

    // ACTUALIZADA: Exportar tabla principal a PDF con proveedor
    const exportTableToPDF = () => {
        try {
            const doc = new jsPDF('l', 'pt', 'a4');
            const img = new Image();
            img.src = '/dist/img/bbtt2.png';
            img.onload = function() {
                doc.addImage(img, 'PNG', 40, 20, 65, 65);

                doc.setProperties({
                    title: 'Reporte de Facturas de Entrada BBT-SIS',
                    subject: 'Facturas de entrada registradas',
                    author: 'Sistema de Inventarios',
                    keywords: 'facturas, entrada, reporte, pdf',
                    creator: 'Sistema de Inventarios'
                });

                doc.setFontSize(18);
                doc.setTextColor(40);
                doc.text('Reporte de Facturas de Entrada BBT-SIS', 150, 40);
                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 150, 60);
                
                let yPosition = 75;
                if (referenciaFilter) {
                    doc.text(`Filtro por referencia: "${referenciaFilter}"`, 150, yPosition);
                    yPosition += 15;
                }
                if (proveedorFilter) {
                    doc.text(`Filtro por proveedor: "${proveedorFilter}"`, 150, yPosition);
                    yPosition += 15;
                }
                doc.text(`Total de facturas: ${filteredEntradas.length}`, 150, yPosition);

                const tableData = filteredEntradas.map(entrada => {
                    const totalProductos = entrada.entradaDetalle?.length || 0;
                    const totalCantidad = (entrada.entradaDetalle || []).reduce((sum: number, detalle: any) => sum + detalle.cantidad, 0);
                    
                    return [
                        entrada.folio,
                        formatDate(entrada.fecha),
                        entrada.proveedor || '-', // NUEVO: Columna de proveedor
                        entrada.referencia || '-',
                        totalProductos,
                        totalCantidad,
                        entrada.observaciones || '-'
                    ];
                });

                autoTable(doc, {
                    head: [['Folio', 'Fecha', 'Proveedor', 'Referencia', 'Total Productos', 'Total Cantidad', 'Observaciones']], // ACTUALIZADO: Header con proveedor
                    body: tableData,
                    startY: yPosition + 15,
                    margin: { horizontal: 40 },
                    styles: {
                        fontSize: 8,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        valign: 'middle'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 9
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 50, halign: 'center' },  // Folio
                        1: { cellWidth: 70, halign: 'center' },  // Fecha
                        2: { cellWidth: 100, halign: 'center' }, // Proveedor
                        3: { cellWidth: 90, halign: 'center' },  // Referencia
                        4: { cellWidth: 70, halign: 'center' },  // Total Productos
                        5: { cellWidth: 70, halign: 'center' },  // Total Cantidad
                        6: { cellWidth: 'auto', halign: 'left' } // Observaciones
                    },
                    didDrawPage: function (data: any) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(
                            `Página ${currentPage} de ${pageCount}`,
                            data.settings.margin.left,
                            doc.internal.pageSize.height - 20
                        );
                    }
                });

                const filtroTexto = referenciaFilter || proveedorFilter ? 
                    `_filtrado_${referenciaFilter || ''}${proveedorFilter || ''}` : '';
                doc.save(`facturas_entrada${filtroTexto}_${new Date().toISOString().slice(0, 10)}.pdf`);

                toast.current?.show({
                    severity: 'success',
                    summary: 'Exportación exitosa',
                    detail: 'Archivo PDF generado correctamente',
                    life: 3000
                });
            };
            img.onerror = function() {
                console.error('Error al cargar la imagen del membretado');
                const filtroTexto = referenciaFilter || proveedorFilter ? 
                    `_filtrado_${referenciaFilter || ''}${proveedorFilter || ''}` : '';
                doc.save(`facturas_entrada${filtroTexto}_${new Date().toISOString().slice(0, 10)}.pdf`);
            };
        } catch (error) {
            console.error('Error al generar PDF:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Ocurrió un error al generar el PDF',
                life: 3000
            });
        }
    };

    // Funciones existentes para exportar detalles del modal (mejoradas)
    const exportToExcel = () => {
        if (!selectedMovimiento) return;

        const detalle = selectedMovimiento.entradaDetalle || [];

        const excelData = detalle.map((detalle: any) => ({
            Clave: detalle.cve_producto,
            Descripción: getDescripcionProducto(detalle.cve_producto),
            Cantidad: detalle.cantidad
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Detalle de Factura');
        const fileName = `detalle_factura_entrada_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        toast.current?.show({
            severity: 'success',
            summary: 'Exportación exitosa',
            detail: 'Detalle exportado a Excel correctamente',
            life: 3000
        });
    };

    const exportToPdf = () => {
        if (!selectedMovimiento) return;

        const doc = new jsPDF('l', 'pt', 'a4');
        const img = new Image();
        img.src = '/dist/img/bbtt2.png';
        img.onload = function () {
            doc.addImage(img, 'PNG', 40, 20, 65, 65);

            doc.setProperties({
                title: 'Detalle de Factura de Entrada',
                subject: 'Detalles de Entrada',
                author: 'Sistema de Inventarios',
                keywords: 'factura, entrada, detalle, pdf',
                creator: 'Sistema de Inventarios'
            });

            doc.setFontSize(18);
            doc.setTextColor(40);
            doc.text('Detalle de Factura de Entrada', 150, 40);
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 150, 60);

            let y = 100;

            doc.setFontSize(12);
            doc.text(`Folio:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.folio}`, 75, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Fecha:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${formatDate(selectedMovimiento.fecha)}`, 85, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            // NUEVO: Mostrar proveedor en el PDF del detalle
            doc.text(`Proveedor:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.proveedor || '-'}`, 100, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Referencia:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.referencia || '-'}`, 106, y);
            doc.setFont('helvetica', 'normal');
            y += 18;
            doc.text(`Observaciones:`, 40, y);
            doc.setFont('helvetica', 'bold');
            doc.text(`${selectedMovimiento.observaciones || '-'}`, 130, y);
            doc.setFont('helvetica', 'normal');
            y += 18;

            const tableData = selectedMovimiento.entradaDetalle || [];
            const pdfData = tableData.map((detalle: any) => [
                detalle.cve_producto,
                getDescripcionProducto(detalle.cve_producto),
                detalle.cantidad
            ]);

            autoTable(doc, {
                head: [['Clave', 'Descripción', 'Cantidad']],
                body: pdfData,
                startY: y + 20,
                margin: { horizontal: 40 },
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    valign: 'middle'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    fontSize: 9
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 180 },
                    1: { cellWidth: 300 },
                    2: { cellWidth: 100 }
                },
                didDrawPage: function (data: any) {
                    const pageCount = doc.getNumberOfPages();
                    const currentPage = data.pageNumber;
                    doc.setFontSize(10);
                    doc.setTextColor(150);
                    doc.text(`Página ${currentPage} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 20);
                }
            });

            doc.save(`detalle_factura_entrada_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.pdf`);

            toast.current?.show({
                severity: 'success',
                summary: 'Exportación exitosa',
                detail: 'Detalle exportado a PDF correctamente',
                life: 3000
            });
        };
    };

    // ACTUALIZADA: Función para limpiar filtros
    const limpiarFiltros = () => {
        setReferenciaFilter('');
        setProveedorFilter('');
    };

    return (
        <div>
            <Toast ref={toast} />
            <Navbar />
            <Sidebar />
            
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                
                {/* Header principal */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{ 
                            margin: 0, 
                            color: '#1f2937', 
                            fontSize: '2rem', 
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            📋 Consulta de Facturas (Entradas)
                        </h1>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                        }}>
                            <i className="pi pi-file-import"></i>
                            {loading ? 'CARGANDO...' : `${filteredEntradas.length} FACTURAS`}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #10b981, #3b82f6)', borderRadius: '1px' }}></div>
                </div>

                {/* ACTUALIZADO: Panel de filtro y exportación con filtro de proveedor */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ 
                            margin: 0, 
                            color: '#374151', 
                            fontSize: '1.2rem', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            🔍 Filtros y Exportación
                        </h3>
                        
                        {/* Botones de exportación */}
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <Button
                                icon="pi pi-file-excel"
                                label="Exportar Excel"
                                severity="success"
                                onClick={exportTableToExcel}
                                disabled={filteredEntradas.length === 0 || loading}
                                style={{ 
                                    height: '40px',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    borderRadius: '8px'
                                }}
                                tooltip="Exportar tabla completa a Excel"
                            />
                            <Button
                                icon="pi pi-file-pdf"
                                label="Exportar PDF"
                                severity="danger"
                                onClick={exportTableToPDF}
                                disabled={filteredEntradas.length === 0 || loading}
                                style={{ 
                                    height: '40px',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    borderRadius: '8px'
                                }}
                                tooltip="Exportar tabla completa a PDF"
                            />
                        </div>
                    </div>

                    {/* ACTUALIZADO: Filtros con referencia y proveedor */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px', alignItems: 'end' }}>
                        <div>
                            <label style={{ 
                                display: 'block', 
                                fontSize: '0.9rem', 
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                📄 Referencia de Factura
                            </label>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-search" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    placeholder="Filtrar por referencia..."
                                    value={referenciaFilter}
                                    onChange={onReferenciaFilterChange}
                                    style={{ 
                                        width: '100%', 
                                        height: '40px',
                                        paddingLeft: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px'
                                    }}
                                />
                            </div>
                        </div>

                        {/* NUEVO: Filtro por proveedor */}
                        <div>
                            <label style={{ 
                                display: 'block', 
                                fontSize: '0.9rem', 
                                fontWeight: '600',
                                marginBottom: '6px',
                                color: '#374151'
                            }}>
                                🏢 Proveedor
                            </label>
                            <div style={{ position: 'relative' }}>
                                <i className="pi pi-building" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '0.9rem'
                                }}></i>
                                <InputText
                                    placeholder="Filtrar por proveedor..."
                                    value={proveedorFilter}
                                    onChange={onProveedorFilterChange}
                                    style={{ 
                                        width: '100%', 
                                        height: '40px',
                                        paddingLeft: '40px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '8px'
                                    }}
                                />
                            </div>
                        </div>

                        <Button
                            icon="pi pi-refresh"
                            label="Limpiar Filtros"
                            size="small"
                            severity="secondary"
                            outlined
                            onClick={limpiarFiltros}
                            style={{ borderRadius: '8px', height: '40px' }}
                        />
                    </div>

                    {/* ACTUALIZADO: Indicador de filtros activos */}
                    {(referenciaFilter || proveedorFilter) && (
                        <div style={{ 
                            marginTop: '16px',
                            padding: '12px 16px',
                            backgroundColor: '#f0f9ff',
                            borderRadius: '8px',
                            border: '1px solid #bae6fd',
                            fontSize: '0.9rem',
                            color: '#0c4a6e',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap'
                        }}>
                            <i className="pi pi-filter" style={{ color: '#0284c7' }}></i>
                            <span><strong>Filtros activos:</strong></span>
                            {referenciaFilter && (
                                <span style={{ 
                                    backgroundColor: '#e0f2fe', 
                                    padding: '4px 8px', 
                                    borderRadius: '12px',
                                    fontSize: '0.8rem'
                                }}>
                                    Referencia: "{referenciaFilter}"
                                </span>
                            )}
                            {proveedorFilter && (
                                <span style={{ 
                                    backgroundColor: '#e8f5e8', 
                                    padding: '4px 8px', 
                                    borderRadius: '12px',
                                    fontSize: '0.8rem'
                                }}>
                                    Proveedor: "{proveedorFilter}"
                                </span>
                            )}
                            <Badge value={filteredEntradas.length} severity="info" />
                        </div>
                    )}
                </div>

                {/* Mensaje de carga */}
                {loading && (
                    <div style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        padding: '20px',
                        borderRadius: '12px',
                        marginBottom: '20px',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>⏳</div>
                        <strong style={{ color: '#059669' }}>Cargando facturas de entrada...</strong>
                    </div>
                )}
                
                {/* Mensaje de error */}
                {error && (
                    <div style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        color: '#dc2626',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <i className="pi pi-exclamation-triangle"></i>
                        {error}
                    </div>
                )}

                {/* Resumen de facturas */}
                {filteredEntradas.length > 0 && !loading && !error && (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '12px',
                        padding: '24px',
                        marginBottom: '20px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <h3 style={{ 
                            margin: '0 0 20px 0', 
                            color: '#374151', 
                            fontSize: '1.3rem', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📊 Resumen de Facturas de Entrada
                        </h3>
                        
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px'
                        }}>
                            <div style={{
                                backgroundColor: '#dcfce7',
                                padding: '20px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '2px solid #16a34a'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📋</div>
                                <div style={{ fontSize: '0.9rem', color: '#166534', fontWeight: '600', marginBottom: '4px' }}>TOTAL FACTURAS</div>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#16a34a' }}>
                                    {filteredEntradas.length}
                                </div>
                            </div>
                            
                            <div style={{
                                backgroundColor: '#dbeafe',
                                padding: '20px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '2px solid #3b82f6'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📦</div>
                                <div style={{ fontSize: '0.9rem', color: '#1e40af', fontWeight: '600', marginBottom: '4px' }}>TOTAL PRODUCTOS</div>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#3b82f6' }}>
                                    {filteredEntradas.reduce((total, entrada) => 
                                        total + (entrada.entradaDetalle?.length || 0), 0
                                    )}
                                </div>
                            </div>

                            <div style={{
                                backgroundColor: '#fef3c7',
                                padding: '20px',
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: '2px solid #f59e0b'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📈</div>
                                <div style={{ fontSize: '0.9rem', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>TOTAL CANTIDAD</div>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#f59e0b' }}>
                                    {filteredEntradas.reduce((total, entrada) => 
                                        total + (entrada.entradaDetalle || []).reduce((sum: number, detalle: any) => sum + detalle.cantidad, 0), 0
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ACTUALIZADA: Tabla de facturas con columna de proveedor */}
                {filteredEntradas.length > 0 && !loading && (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <h3 style={{ 
                            margin: '0 0 16px 0', 
                            color: '#374151', 
                            fontSize: '1.2rem', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📄 Facturas de Entrada
                            {(referenciaFilter || proveedorFilter) && (
                                <span style={{ 
                                    fontSize: '0.9rem', 
                                    color: '#10b981',
                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                    fontWeight: '500'
                                }}>
                                    Filtrado
                                </span>
                            )}
                        </h3>
                        
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb'
                        }}>
                            <DataTable
                                value={filteredEntradas}
                                responsiveLayout="scroll"
                                stripedRows
                                size="small"
                                emptyMessage="📭 No hay facturas registradas"
                                style={{ fontSize: '0.875rem' }}
                                onRowClick={(e) => showMovimientoDetail(e.data as EntradaCompleta)}
                                rowHover
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} facturas"
                            >
                                <Column field="folio" header="📄 Folio" style={{ width: '100px', textAlign: 'center' }} sortable />
                                <Column
                                    field="fecha"
                                    header="📅 Fecha"
                                    body={(rowData) => formatDate(rowData.fecha)}
                                    style={{ width: '120px', textAlign: 'center' }}
                                    sortable
                                />
                                {/* NUEVA: Columna de proveedor */}
                                <Column 
                                    field="proveedor" 
                                    header="🏢 Proveedor" 
                                    body={(rowData) => rowData.proveedor || 'Sin proveedor'}
                                    style={{ width: '180px' }}
                                    sortable
                                />
                                <Column 
                                    field="referencia" 
                                    header="🔗 Referencia" 
                                    body={(rowData) => rowData.referencia || '-'}
                                    style={{ width: '150px' }}
                                    sortable
                                />
                                <Column 
                                    header="📊 Total Productos" 
                                    body={(rowData) => (
                                        <div style={{ textAlign: 'center' }}>
                                            <Badge value={rowData.entradaDetalle?.length || 0} severity="info" />
                                        </div>
                                    )}
                                    style={{ width: '120px', textAlign: 'center' }}
                                />
                                <Column 
                                    header="📦 Total Cantidad" 
                                    body={(rowData) => {
                                        const total = (rowData.entradaDetalle || []).reduce((sum: number, detalle: any) => sum + detalle.cantidad, 0);
                                        return (
                                            <div style={{ textAlign: 'center' }}>
                                                <Badge value={total} severity="success" />
                                            </div>
                                        );
                                    }}
                                    style={{ width: '120px', textAlign: 'center' }}
                                />
                                <Column field="observaciones" header="📝 Observación" />
                            </DataTable>
                        </div>
                    </div>
                )}

                {/* Mensaje cuando no hay facturas */}
                {filteredEntradas.length === 0 && !loading && !error && (
                    <div style={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.1)', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        marginBottom: '20px',
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{ fontSize: '1.5rem' }}>📭</div>
                        <div>
                            <strong style={{ color: '#1e40af' }}>Sin Facturas:</strong>
                            <span style={{ color: '#374151', marginLeft: '8px' }}>
                                {(referenciaFilter || proveedorFilter) ? 
                                    'No hay facturas que coincidan con los filtros aplicados' :
                                    'No hay facturas de entrada registradas en el sistema'
                                }
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* ACTUALIZADO: Modal de detalles con información del proveedor */}
            <Dialog
                header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        📈 Detalles de Factura de Entrada
                    </div>
                }
                visible={isModalVisible}
                style={{ width: '90%' }}
                onHide={() => setIsModalVisible(false)}
            >
                {selectedMovimiento && (
                    <div style={{
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        padding: '20px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h4 style={{ margin: 0, color: '#374151' }}>
                                📈 Encabezado de Entrada
                            </h4>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    icon="pi pi-file-excel"
                                    label="Excel"
                                    size="small"
                                    severity="success"
                                    onClick={exportToExcel}
                                    style={{ borderRadius: '8px' }}
                                    tooltip="Exportar detalle a Excel"
                                />
                                <Button
                                    icon="pi pi-file-pdf"
                                    label="PDF"
                                    size="small"
                                    severity="danger"
                                    onClick={exportToPdf}
                                    style={{ borderRadius: '8px' }}
                                    tooltip="Exportar detalle a PDF"
                                />
                            </div>
                        </div>
                        
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            marginBottom: '20px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600', width: '150px' }}>📄 Folio</th>
                                        <td style={{ padding: '12px' }}>{selectedMovimiento.folio}</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📅 Fecha</th>
                                        <td style={{ padding: '12px' }}>{formatDate(selectedMovimiento.fecha)}</td>
                                    </tr>
                                    {/* NUEVA: Fila del proveedor en el modal */}
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>🏢 Proveedor</th>
                                        <td style={{ padding: '12px' }}>{selectedMovimiento.proveedor || 'Sin proveedor'}</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>🔗 Referencia</th>
                                        <td style={{ padding: '12px' }}>{selectedMovimiento.referencia || '-'}</td>
                                    </tr>
                                    <tr>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📝 Observaciones</th>
                                        <td style={{ padding: '12px' }}>{selectedMovimiento.observaciones || '-'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h4 style={{ margin: '0 0 16px 0', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            📋 Detalle de Productos
                        </h4>
                        
                        <div style={{ 
                            maxHeight: '400px', 
                            overflowY: 'auto', 
                            overflowX: 'auto',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <table style={{ 
                                width: '100%', 
                                borderCollapse: 'collapse',
                                fontSize: '0.875rem'
                            }}>
                                <thead style={{ 
                                    position: 'sticky', 
                                    top: 0, 
                                    zIndex: 10,
                                    backgroundColor: '#374151',
                                    color: 'white'
                                }}>
                                    <tr>
                                        <th style={{ minWidth: '100px', textAlign: 'center', padding: '12px' }}>🔑 Clave</th>
                                        <th style={{ minWidth: '200px', textAlign: 'center', padding: '12px' }}>📝 Descripción</th>
                                        <th style={{ minWidth: '60px', width: '60px', textAlign: 'center', padding: '12px' }}>📊 Cant.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const detalleArray = selectedMovimiento.entradaDetalle || [];
                                        
                                        if (detalleArray.length === 0) {
                                            return (
                                                <tr>
                                                    <td colSpan={3} style={{ 
                                                        textAlign: 'center', 
                                                        padding: '20px', 
                                                        fontStyle: 'italic', 
                                                        color: '#6b7280',
                                                        backgroundColor: '#f8f9fa'
                                                    }}>
                                                        📭 No hay detalles disponibles
                                                    </td>
                                                </tr>
                                            );
                                        }
                                        
                                        return detalleArray.map((detalle: any, index: number) => (
                                            <tr key={index} style={{
                                                transition: 'background-color 0.2s',
                                                borderBottom: '1px solid #e5e7eb'
                                            }}>
                                                <td style={{
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle',
                                                    fontWeight: '500',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '12px'
                                                }}>
                                                    {detalle.cve_producto}
                                                </td>
                                                <td style={{
                                                    verticalAlign: 'middle',
                                                    paddingLeft: '12px',
                                                    wordWrap: 'break-word',
                                                    padding: '12px'
                                                }}>
                                                    {getDescripcionProducto(detalle.cve_producto)}
                                                </td>
                                                <td style={{
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle',
                                                    fontWeight: 'bold',
                                                    color: '#3b82f6',
                                                    width: '60px',
                                                    padding: '12px'
                                                }}>
                                                    <Badge value={detalle.cantidad} severity="info" />
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default Facturas;